import { workflowStepView, type WorkflowDefinition } from "../../../api/content/processDefinitions";
import type { ApiSchema } from "../../../api/apiSchema";
import type { WorkflowContent, WorkflowInputDeclaration } from "../../../api/content/authoring/processContentDecoders";

/**
 * The graph editor's model (authoring-ui.md AU-6): `workflowDraft(definition, action)`
 * returns the next definition. Plain data in, plain data out; React Flow only reports
 * gestures and draws the result. It edits the Workflow's own steps (roles, start policy,
 * input bindings), its sub-workflow uses, its exports and its inputs' defaults; never the
 * inside of a sub-workflow, and never the structure kind (sequence only, W-7).
 *
 * Order matters: a binding may only name an earlier step. Input steps are placed before
 * the first main step, main steps after the last one.
 */

export type WorkflowStepEntry = ApiSchema<"WorkflowStep">;
export type StepUseEntry = ApiSchema<"StepUse">;
export type SubWorkflowUseEntry = ApiSchema<"SubWorkflowUse">;
export type AbstractStepEntry = ApiSchema<"AbstractStepUse">;
export type Binding = ApiSchema<"InputBinding">;
export type WorkflowRole = ApiSchema<"WorkflowRole">;
export type StepStart = ApiSchema<"StepStart">;

/** Between a destination's flat step key and its input (`<flat key>.<input>`). */
const DESTINATION_SEPARATOR = ".";

export type WorkflowDraftAction =
  /** A pinned step or Workflow version; `name` suggests its key. */
  | { readonly type: "add"; readonly use: { readonly kind: "step" | "workflow"; readonly reference: string }; readonly role: WorkflowRole; readonly name: string }
  | { readonly type: "remove"; readonly key: string }
  /** Move a step before another one, or to the end with `null`. */
  | { readonly type: "move"; readonly key: string; readonly before: string | null }
  | { readonly type: "setRole"; readonly key: string; readonly role: WorkflowRole }
  | { readonly type: "setStart"; readonly key: string; readonly start: StepStart }
  | { readonly type: "bind"; readonly key: string; readonly slot: string; readonly from: Binding }
  | { readonly type: "unbind"; readonly key: string; readonly slot: string }
  /** Pin another version of the same step or Workflow, such as one the step form just made. */
  /** Every placement that pins `from` pins `to` instead: a step saved from the editor's drawer is one copy for all its placements in this Workflow, as module B's cascade makes it (ADR-0037 as built, WD-1+3 D-8). */
  | { readonly type: "repin"; readonly from: string; readonly to: string }
  | { readonly type: "setExport"; readonly name: string; readonly from: Binding }
  | { readonly type: "removeExport"; readonly name: string }
  /**
   * The Workflow default of the step input `destination` (`<key>.<input>`), or none with `null`
   * (WD-1+3 S4-G1). Module B owns which inputs a Workflow stores: the draft's stored inputs take
   * the default where they hold the input; otherwise `declared`, B's list for the draft, is
   * stored with it, so every input reading the draft derived stays exposed.
   */
  | { readonly type: "setDefault"; readonly destination: string; readonly value: WorkflowInputDeclaration["default"]; readonly declared: readonly WorkflowInputDeclaration[] | null };

export function workflowDraft(definition: WorkflowContent, action: WorkflowDraftAction): WorkflowContent {
  switch (action.type) {
    case "add":
      return addStep(definition, action.use, action.role, action.name);
    case "remove":
      return removeStep(definition, action.key);
    case "move":
      return moveStep(definition, action.key, action.before);
    case "setRole":
      return withStep(definition, action.key, (step) => ({ ...step, role: action.role }));
    case "setStart":
      return withStepUse(definition, action.key, (step) => ({ ...step, start: action.start }));
    case "bind":
      return withStepUse(definition, action.key, (step) => ({ ...step, input_bindings: { ...step.input_bindings, [action.slot]: action.from } }));
    case "unbind":
      return withStepUse(definition, action.key, (step) => ({ ...step, input_bindings: without(step.input_bindings, action.slot) }));
    case "repin":
      return {
        ...definition,
        // An abstract step pins nothing; what a use binds to one is not edited here (ADR-0043).
        steps: definition.steps.map((step) => (isStepUse(step) ? (step.step === action.from ? { ...step, step: action.to } : step) : isSubWorkflowUse(step) && step.workflow === action.from ? { ...step, workflow: action.to } : step)),
      };
    case "setExport":
      return { ...definition, exports: { ...definition.exports, [action.name]: action.from } };
    case "removeExport":
      return { ...definition, exports: without(definition.exports, action.name) };
    case "setDefault":
      return withDefault(definition, action.destination, action.value, action.declared);
  }
}

function withDefault(definition: WorkflowContent, destination: string, value: WorkflowInputDeclaration["default"], declared: readonly WorkflowInputDeclaration[] | null): WorkflowContent {
  const stored = definition.declared_inputs ?? [];
  const holds = (inputs: readonly WorkflowInputDeclaration[]) => inputs.some((input) => input.destination === destination);
  let inputs: readonly WorkflowInputDeclaration[];
  if (holds(stored)) {
    inputs = stored;
  } else if (value !== null && declared !== null && holds(declared)) {
    // B's list may be a check behind the draft (Sol r1): the stored inputs keep their edits, and B's join them only for steps the draft still has.
    const keys = new Set(definition.steps.map((step) => step.key));
    const kept = new Set(stored.map((input) => input.destination));
    inputs = [...stored, ...declared.filter((input) => !kept.has(input.destination) && keys.has(stepKeyOf(input.destination)))];
  } else {
    // No default where the draft stores none is no change: nothing is stored for it.
    return definition;
  }
  return { ...definition, declared_inputs: inputs.map((input) => (input.destination === destination ? { ...input, default: value } : input)) };
}

/** The Workflow's own step a destination (`<flat key>.<input>`) names: its first key; a sub-workflow's step is below it. */
function stepKeyOf(destination: string): string {
  return destination.split(DESTINATION_SEPARATOR, 1)[0] ?? destination;
}

/**
 * The draft as it is saved, against the version the editor `opened` (WD-1+3 S4-G1, Sol r1 to
 * r3). An input a wire fills is no Workflow input, so a run isn't asked for it: it is stored
 * only while no wire fills it. The opened version's inputs come first, in their order and
 * with the draft's defaults, so wiring one and taking the wire back changes nothing; then
 * those this session stored. A Workflow opened without stored inputs, whose inputs reading it
 * derives, stores none while it gives no default, so taking a default back leaves no change.
 */
export function settledInputs(draft: WorkflowContent, opened: WorkflowContent): WorkflowContent {
  const stored = draft.declared_inputs ?? null;
  const openedInputs = opened.declared_inputs ?? null;
  if (stored === null && openedInputs === null) return draft;
  const keys = new Set(draft.steps.map((step) => step.key));
  const wired = new Set(draft.steps.flatMap((step) => (isSubWorkflowUse(step) ? [] : Object.keys(step.input_bindings).map((slot) => `${step.key}${DESTINATION_SEPARATOR}${slot}`))));
  const open = (input: WorkflowInputDeclaration) => !wired.has(input.destination) && keys.has(stepKeyOf(input.destination));
  const current = new Map((stored ?? []).map((input) => [input.destination, input]));
  const fromOpened = new Set((openedInputs ?? []).map((input) => input.destination));
  const inputs = [
    ...(openedInputs ?? []).filter(open).map((input) => current.get(input.destination) ?? input),
    ...(stored ?? []).filter((input) => !fromOpened.has(input.destination) && open(input)),
  ];
  if (openedInputs === null && !inputs.some((input) => input.default !== null && input.default !== undefined)) {
    const { declared_inputs: _derivedOnRead, ...withoutInputs } = draft;
    return withoutInputs;
  }
  return stored !== null && inputs.length === stored.length && inputs.every((input, index) => input === stored[index]) ? draft : { ...draft, declared_inputs: inputs };
}

export function isStepUse(step: WorkflowStepEntry): step is StepUseEntry {
  return "step" in step;
}

export function isSubWorkflowUse(step: WorkflowStepEntry): step is SubWorkflowUseEntry {
  return "workflow" in step;
}

export function isAbstractStep(step: WorkflowStepEntry): step is AbstractStepEntry {
  return "accepts" in step;
}

/** The pinned version a Workflow step names; `null` for an abstract step, which pins none (ADR-0043 D1). */
export function referenceOf(step: WorkflowStepEntry): string | null {
  return isStepUse(step) ? step.step : isSubWorkflowUse(step) ? step.workflow : null;
}

/** Step keys (W-7): lower case, digits and `_`, starting with a letter, at most 63. */
export function keyFromName(name: string, taken: ReadonlySet<string>): string {
  const base =
    name
      .normalize("NFKD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .replace(/^(?=[0-9])/, "step_")
      .slice(0, 56) || "step";
  let key = base;
  for (let suffix = 2; taken.has(key); suffix += 1) {
    key = `${base}_${String(suffix)}`;
  }
  return key;
}

function addStep(definition: WorkflowContent, use: { readonly kind: "step" | "workflow"; readonly reference: string }, role: WorkflowRole, name: string): WorkflowContent {
  const key = keyFromName(name, new Set(definition.steps.map((step) => step.key)));
  const entry: WorkflowStepEntry = use.kind === "step" ? { key, role, step: use.reference, start: "automatic", input_bindings: {} } : { key, role, workflow: use.reference };
  const steps = [...definition.steps];
  const firstMain = steps.findIndex((step) => step.role === "main");
  // An input prepares what the pipeline reads, so it runs before the pipeline starts.
  steps.splice(role === "input" && firstMain >= 0 ? firstMain : steps.length, 0, entry);
  return { ...definition, steps };
}

/** A removed step takes the bindings, exports and outcome conditions that named it with it. */
function removeStep(definition: WorkflowContent, key: string): WorkflowContent {
  const steps = definition.steps.filter((step) => step.key !== key).map((step) => withoutWiresFrom(step, key));
  const exports = Object.fromEntries(Object.entries(definition.exports).filter(([, binding]) => binding.from_step !== key));
  const conditions = definition.outcome_conditions?.filter((condition) => condition.step !== key);
  // A stored input of the removed step goes with it (WD-1+3 S4-G1); a Workflow that stores none still stores none.
  const inputs = definition.declared_inputs === undefined || definition.declared_inputs === null ? {} : { declared_inputs: definition.declared_inputs.filter((input) => !input.destination.startsWith(`${key}.`)) };
  if (conditions === undefined || conditions.length > 0) return { ...definition, steps, exports, ...inputs, ...(conditions === undefined ? {} : { outcome_conditions: conditions }) };
  // The last condition gone, the Workflow declares none (W-22): B refuses an empty declaration.
  const { outcome_conditions: _removed, ...declaresNone } = definition;
  return { ...declaresNone, steps, exports, ...inputs };
}

/** A placement without the wires that read step `key`: its own, and a use's parent wiring and bindings' (ADR-0043 D2). */
function withoutWiresFrom(step: WorkflowStepEntry, key: string): WorkflowStepEntry {
  if (!isSubWorkflowUse(step)) {
    return { ...step, input_bindings: wiresNotFrom(step.input_bindings, key) };
  }
  const { input_bindings: wires, definitions, ...use } = step;
  const bound = definitions == null ? { definitions } : { definitions: Object.fromEntries(Object.entries(definitions).map(([abstractKey, { input_bindings: bindingWires, ...binding }]) => [abstractKey, { ...binding, ...optionalWires(bindingWires, key) }])) };
  return { ...use, ...optionalWires(wires, key), ...(definitions === undefined ? {} : bound) };
}

function wiresNotFrom(wires: Readonly<Record<string, Binding>>, key: string): Record<string, Binding> {
  return Object.fromEntries(Object.entries(wires).filter(([, binding]) => binding.from_step !== key));
}

/** Optional wires without those from `key`, left out once none is left: B refuses an empty set (ADR-0043 D2). */
function optionalWires(wires: Readonly<Record<string, Binding>> | null | undefined, key: string): { input_bindings?: Record<string, Binding> | null } {
  if (wires == null) {
    return wires === undefined ? {} : { input_bindings: null };
  }
  const kept = wiresNotFrom(wires, key);
  return Object.keys(kept).length === 0 ? {} : { input_bindings: kept };
}

function moveStep(definition: WorkflowContent, key: string, before: string | null): WorkflowContent {
  const moved = definition.steps.find((step) => step.key === key);
  if (moved === undefined || before === key) {
    return definition;
  }
  const rest = definition.steps.filter((step) => step.key !== key);
  const index = before === null ? rest.length : rest.findIndex((step) => step.key === before);
  if (index < 0) {
    return definition;
  }
  return { ...definition, steps: [...rest.slice(0, index), moved, ...rest.slice(index)] };
}

function withStep(definition: WorkflowContent, key: string, change: (step: WorkflowStepEntry) => WorkflowStepEntry): WorkflowContent {
  return { ...definition, steps: definition.steps.map((step) => (step.key === key ? change(step) : step)) };
}

function withStepUse(definition: WorkflowContent, key: string, change: (step: StepUseEntry) => StepUseEntry): WorkflowContent {
  return withStep(definition, key, (step) => (isStepUse(step) ? change(step) : step));
}

function without<T>(record: Readonly<Record<string, T>>, name: string): Record<string, T> {
  return Object.fromEntries(Object.entries(record).filter(([member]) => member !== name));
}

/** A Workflow draft as the view model reads it. */
export function definitionOfDraft(draft: WorkflowContent, reference: string): WorkflowDefinition {
  return {
    reference,
    name: draft.name,
    description: draft.description,
    exports: Object.fromEntries(Object.entries(draft.exports).map(([name, binding]) => [name, { fromStep: binding.from_step, part: binding.part }])),
    steps: draft.steps.map(workflowStepView),
    startInputs: (draft.declared_inputs ?? []).map((input) => input.destination),
  };
}
